// Voice engines → Rollout (plan §6.5.1): the env read-outs, the draft of the
// Remote Config `voice_engine` policy with its preview, publish and history.
//
// Publishing: a fresh server preview checks the draft, the review dialog
// shows the diff, and PUT /policy publishes over the ETag the page loaded
// (If-Match). A 412 means someone published since: the page takes their
// value as the new base, keeps the draft, and shows what it now changes.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ApiError } from "../../services/http";
import {
  getPolicyHistory,
  previewPolicy,
  publishPolicy,
  restorePolicy,
  type DiffRow,
  type HistoryEntry,
  type LookupRequest,
  type Overview,
  type PolicyHistory,
  type PolicyState,
  type Preview,
  type Profile,
  type PublishResult,
} from "../../services/voiceEngines";
import { Modal } from "../Modal";
import { formatDateTime } from "../analytics/format";
import { ArmsEditor } from "./ArmsEditor";
import { HistoryPanel, PublisherBadge } from "./HistoryPanel";
import { DistributionPanel, LookupPanel } from "./PreviewPanel";
import { Callout, DiffList, Messages } from "./parts";
import { detailErrors, errorText, isForbidden } from "./errors";
import { ENGINE_ORDER } from "./labels";
import {
  draftErrors,
  draftFromPolicy,
  draftToPayload,
  draftToPolicy,
  nextExperimentKey,
  policyDiff,
  serverDiffLine,
  type DiffLine,
  type Draft,
} from "./policy";

const PREVIEW_DEBOUNCE_MS = 700;
const NOTE_MAX = 200;

interface Props {
  overview: Overview;
  profiles: Profile[];
  canWrite: boolean;
  userEmail: string | null;
  onForbidden: (message: string) => void;
  onPublished: () => Promise<void>;
}

export function RolloutTab({ overview, profiles, canWrite, userEmail, onForbidden, onPublished }: Props) {
  const { env } = overview;
  // The published value this draft is edited against; a 412 replaces it.
  const [live, setLive] = useState<PolicyState | null>(overview.policy);
  const base = live?.policy ?? overview.inForce.policy;
  const [draft, setDraft] = useState<Draft>(() => draftFromPolicy(base));
  const [loadedFrom, setLoadedFrom] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  // A 412: the server's diff, and the version being restored (null for a publish).
  const [conflict, setConflict] = useState<{ rows: DiffLine[]; restore: string | null } | null>(null);
  const [note, setNote] = useState("");

  const [preview, setPreview] = useState<Preview | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [lookup, setLookup] = useState<LookupRequest | null>(null);
  // The lookup the shown preview ran with, so a new lookup never shows the last person's ladder.
  const [previewFor, setPreviewFor] = useState<LookupRequest | null>(null);
  const [lookupError, setLookupError] = useState<string | null>(null);
  const previewSeq = useRef(0);

  const [history, setHistory] = useState<PolicyHistory | null>(null);
  const [historyError, setHistoryError] = useState<string | null>(null);

  const [review, setReview] = useState<Preview | null>(null);
  const [checking, setChecking] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [publishErrors, setPublishErrors] = useState<string[]>([]);
  const [restoring, setRestoring] = useState<HistoryEntry | null>(null);
  const [restoreNote, setRestoreNote] = useState("");

  // A reload after a publish brings a new live value: start the draft from it.
  useEffect(() => {
    setLive(overview.policy);
    setDraft(draftFromPolicy(overview.policy?.policy ?? overview.inForce.policy));
    setConflict(null);
    setLoadedFrom(null);
  }, [overview]);

  const loadHistory = useCallback(() => {
    getPolicyHistory()
      .then((h) => {
        setHistory(h);
        setHistoryError(null);
      })
      .catch((e) => setHistoryError(errorText(e)));
  }, []);
  useEffect(loadHistory, [loadHistory]);

  const errors = useMemo(() => draftErrors(draft, profiles), [draft, profiles]);
  const payload = useMemo(() => draftToPayload(draft), [draft]);
  const diff = useMemo(
    () => (errors.length ? [] : policyDiff(live?.policy ?? overview.inForce.policy, draftToPolicy(draft))),
    [errors, live, overview.inForce.policy, draft],
  );
  const changes = diff.filter((d) => d.field !== "cascade (rest)").length;
  // An invalid or missing live value reads as the policy turned off, so an
  // off draft has no diff against it, yet publishing still replaces the value.
  const replacesLive = !!live && (!live.valid || live.raw === null);
  const replacesText = live?.raw === null ? "missing" : "invalid";

  // The server preview of the draft (and the lookup through it), debounced.
  useEffect(() => {
    const seq = ++previewSeq.current;
    if (errors.length) {
      setPreview(null);
      setPreviewFor(null);
      setPreviewing(false);
      return;
    }
    setPreviewing(true);
    const timer = setTimeout(async () => {
      try {
        let result: Preview;
        try {
          result = await previewPolicy(payload, lookup ?? undefined);
        } catch (e) {
          // A lookup that finds nobody fails the whole call: show why, and
          // preview the draft without it.
          if (!lookup || !(e instanceof ApiError) || ![404, 422, 502].includes(e.status)) throw e;
          if (seq === previewSeq.current) {
            setLookupError(errorText(e));
            setLookup(null);
          }
          return;
        }
        if (seq !== previewSeq.current) return;
        setPreview(result);
        setPreviewFor(lookup);
        setPreviewError(null);
        if (lookup) setLookupError(null);
      } catch (e) {
        if (seq === previewSeq.current) setPreviewError(errorText(e));
      } finally {
        if (seq === previewSeq.current) setPreviewing(false);
      }
    }, PREVIEW_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [payload, lookup, errors.length]);

  const edit = (next: Draft) => {
    setDraft(next);
    setToast(null);
  };

  const historyExperiments = (history?.entries ?? []).map((h) => h.policy.experiment ?? "").filter(Boolean);
  const liveExperiment = live?.policy.experiment ?? null;
  const reshuffle =
    draft.enabled && liveExperiment !== null && draft.experiment.trim() !== "" && draft.experiment.trim() !== liveExperiment;

  const blockedReason = !canWrite
    ? `Publishing on ${env.environment} needs your uid in VOICE_ENGINE_ADMIN_UIDS. You can still edit and preview.`
    : !live
      ? "Remote Config could not be read, so there is no value to publish over."
      : live.conditional
        ? "voice_engine has conditional values in the Firebase console. Remove them there before publishing from Studio."
        : errors.length
          ? "Fix the errors above to publish."
          : diff.length === 0 && !replacesLive
            ? "Nothing to publish: the draft matches the live value."
            : !note.trim()
              ? "Say what changed: it goes in the Remote Config description."
              : null;

  const openReview = async () => {
    setChecking(true);
    setPublishErrors([]);
    try {
      const checked = await previewPolicy(payload);
      if (!checked.valid) {
        setPreview(checked);
        setPreviewFor(null);
        return;
      }
      setReview(checked);
    } catch (e) {
      setPreviewError(errorText(e));
    } finally {
      setChecking(false);
    }
  };

  // After a publish or restore: say so, and reload the page's data.
  const published = async (result: PublishResult, what: string) => {
    const warn = result.warnings.length ? ` Check: ${result.warnings.join(" ")}` : "";
    setReview(null);
    setRestoring(null);
    setNote("");
    setRestoreNote("");
    await onPublished();
    setToast(
      `${what} Template v${result.templateVersion.number} is live. Servers pick it up within 60 seconds; calls already running keep their engine.${warn}`,
    );
    loadHistory();
  };

  // Shared failure handling for publish and restore (restore: the version).
  const failed = (e: unknown, restore: string | null = null) => {
    if (isForbidden(e)) {
      onForbidden(errorText(e));
      setReview(null);
      setRestoring(null);
      return;
    }
    if (e instanceof ApiError && e.status === 412 && e.detail && typeof e.detail === "object") {
      const detail = e.detail as { current?: PolicyState; diff?: DiffRow[] };
      if (detail.current) setLive(detail.current);
      setConflict({ rows: (detail.diff ?? []).map(serverDiffLine), restore });
      setReview(null);
      setRestoring(null);
      loadHistory();
      return;
    }
    const list = detailErrors(e);
    setPublishErrors(list.length ? list : [errorText(e)]);
  };

  const publish = async () => {
    if (!live?.etag) return;
    setPublishing(true);
    setPublishErrors([]);
    try {
      const result = await publishPolicy(payload, live.etag, note.trim());
      await published(result, loadedFrom ? `Published the draft loaded from v${loadedFrom}.` : "Published.");
    } catch (e) {
      failed(e);
    } finally {
      setPublishing(false);
    }
  };

  const restore = async () => {
    if (!restoring?.version.number || !live?.etag) return;
    setPublishing(true);
    setPublishErrors([]);
    try {
      const result = await restorePolicy(restoring.version.number, live.etag, restoreNote.trim());
      await published(result, `Restored v${result.restoredFrom}.`);
    } catch (e) {
      failed(e, restoring.version.number);
    } finally {
      setPublishing(false);
    }
  };

  const editFromHistory = (entry: HistoryEntry) => {
    edit(draftFromPolicy(entry.policy));
    setLoadedFrom(entry.version.number);
    setConflict(null);
    window.scrollTo?.({ top: 0, behavior: "smooth" });
  };

  const valueVersion = live?.lastChange?.version ?? live?.templateVersion ?? null;
  const draftWarnings = [
    ...(draft.enabled && draft.arms.length === 0 ? ["No arms: every eligible user gets the cascade."] : []),
    ...(preview?.warnings ?? []),
  ];

  return (
    <div>
      <div className="ve-cards">
        <div className="panel">
          <div className="panel-head">Kill switch</div>
          <div className="ve-row">
            <span className="mono">VOICE_S2S_ENABLED</span>
            <span className={`badge ${env.s2sEnabled ? "ve-ok" : "ve-error"}`}>{env.s2sEnabled ? "on" : "off"}</span>
          </div>
          <div className="ve-help">Env, read-only. Off sends every call to the cascade.</div>
        </div>
        <div className="panel">
          <div className="panel-head">Engines {env.environment} may run</div>
          <div className="chip-row">
            {ENGINE_ORDER.filter((e) => overview.engines.includes(e)).map((e) => {
              const ok = e === "cascade" || env.allowedEngines.includes(e);
              return (
                <span key={e} className={`chip mono${ok ? "" : " ve-off"}`} title={ok ? undefined : "Not allowed here"}>
                  {e}
                </span>
              );
            })}
          </div>
          <div className="ve-help">
            Env <span className="mono">VOICE_S2S_ALLOWED_ENGINES</span>. Changes only with a deploy.
          </div>
        </div>
        <div className="panel">
          <div className="panel-head">Live value</div>
          {valueVersion ? (
            <>
              <div className="ve-row">
                <span style={{ fontWeight: 600 }}>Template v{valueVersion.number}</span>
                <PublisherBadge version={valueVersion} />
              </div>
              <div className="ve-help">
                {valueVersion.by ?? "unknown"} · {formatDateTime(valueVersion.updateTime)}
                {live?.lastChange === null && " · last change not found in recent versions"}
              </div>
            </>
          ) : (
            <div className="muted ve-small">Unavailable</div>
          )}
          {overview.inForce.templateVersion && (
            <div className="ve-help">This server runs template v{overview.inForce.templateVersion}.</div>
          )}
        </div>
      </div>

      {overview.policyError && (
        <Callout tone="error" badge="Remote Config">
          {overview.policyError}. The draft starts from what this server runs; publishing waits until Remote Config can
          be read.
        </Callout>
      )}
      {env.policySource !== "remote_config" && (
        <Callout tone="warn" badge="Env policy">
          This deployment reads the policy from env (<span className="mono">CLIENT_POLICY_SOURCE={env.policySource}</span>
          ), so publishing changes Remote Config but not what this server runs.
        </Callout>
      )}
      {live?.externalPublish && (
        <Callout tone="warn" badge="Console edit">
          The live value was published outside Studio
          {valueVersion?.by ? ` by ${valueVersion.by}` : ""}, so this draft starts from it. Check it before you publish
          over it.
        </Callout>
      )}
      {live?.conditional && (
        <Callout tone="error" badge="Conditions">
          <span className="mono">voice_engine</span> has conditional values in the Firebase console. The server never
          reads them, and Studio refuses to publish until someone removes them there.
        </Callout>
      )}
      {live && !live.valid && (
        <Callout tone="error" badge="Invalid">
          The live value is invalid ({live.error}), so every call is on the cascade (
          <span className="mono">policy_off</span>).
        </Callout>
      )}
      {live?.isV1 && (
        <Callout tone="info" badge="v1 value">
          The live value is the old v1 shape. Studio publishes v2: give the draft an experiment key. Any key reshuffles
          users, because v1 buckets have no experiment.
        </Callout>
      )}
      {toast && (
        <Callout tone="ok" badge="Published" role="status">
          {toast}
        </Callout>
      )}
      {conflict && (
        <Callout tone="warn" badge="Conflict" role="alert">
          Someone published template v{live?.templateVersion.number} while you{" "}
          {conflict.restore ? `restored v${conflict.restore}` : "edited"}. Nothing was published.{" "}
          {conflict.restore
            ? `Against the value now live, restoring v${conflict.restore} changes:`
            : "Your draft is kept; against the value now live it changes:"}
          {conflict.rows.length ? (
            <div style={{ marginTop: 8 }}>
              <DiffList rows={conflict.rows} />
            </div>
          ) : (
            ` nothing in voice_engine (another key changed). ${conflict.restore ? "Restore" : "Review and publish"} again.`
          )}
        </Callout>
      )}
      {loadedFrom && (
        <Callout tone="info" badge="Loaded" role="status">
          The draft now holds template v{loadedFrom}. Publishing saves it as a new version; nothing changes until then.
        </Callout>
      )}

      <div className="ve-columns">
        <section className="panel ve-main" aria-labelledby="ve-draft-h">
          <div className="ve-row" style={{ marginBottom: 8 }}>
            <h2 id="ve-draft-h" className="panel-head" style={{ margin: 0 }}>
              Policy draft
            </h2>
            <span className="spacer" />
            {changes > 0 ? (
              <span className="badge ve-warn">
                {changes} unpublished change{changes === 1 ? "" : "s"}
              </span>
            ) : errors.length ? null : replacesLive ? (
              <span className="badge ve-warn">Replaces the {replacesText} value</span>
            ) : (
              <span className="badge">Same as live</span>
            )}
          </div>
          <label className="ve-check">
            <input type="checkbox" checked={draft.enabled} onChange={(e) => edit({ ...draft, enabled: e.target.checked })} />
            <span>
              <strong>Policy enabled</strong>
              <span className="ve-help" style={{ display: "block" }}>
                Off sends every call to the cascade (<span className="mono">policy_off</span>).
              </span>
            </span>
          </label>
          <div className="ve-fields">
            <div>
              <label htmlFor="ve-ios">Min iOS build</label>
              <input
                id="ve-ios"
                type="number"
                min={0}
                value={draft.min_build_ios}
                onChange={(e) => edit({ ...draft, min_build_ios: e.target.value })}
              />
            </div>
            <div>
              <label htmlFor="ve-android">Min Android build</label>
              <input
                id="ve-android"
                type="number"
                min={0}
                value={draft.min_build_android}
                onChange={(e) => edit({ ...draft, min_build_android: e.target.value })}
              />
            </div>
            <div className="wide">
              <label htmlFor="ve-exp">Experiment key</label>
              <div className="ve-inline">
                <input
                  id="ve-exp"
                  type="text"
                  className="mono"
                  value={draft.experiment}
                  onChange={(e) => edit({ ...draft, experiment: e.target.value.trim() })}
                />
                <button
                  type="button"
                  className="btn"
                  onClick={() =>
                    edit({ ...draft, experiment: nextExperimentKey(draft.experiment, new Date(), historyExperiments) })
                  }
                >
                  New experiment
                </button>
              </div>
            </div>
          </div>
          <div className="ve-help" style={{ marginBottom: 12 }}>
            Older builds stay on the cascade. Users are bucketed by the experiment key: changing a share moves only the
            users at the edge, a new key reshuffles everyone.
          </div>
          {reshuffle && (
            <Callout tone="warn" badge="Reshuffle">
              A new experiment key gives every user a new bucket, so most users switch engines on their next call. Use it
              to start a clean test, not to adjust shares.
            </Callout>
          )}

          <ArmsEditor
            draft={draft}
            live={live?.policy ?? null}
            profiles={profiles}
            allowedEngines={env.allowedEngines}
            distribution={preview?.distribution ?? null}
            onChange={(arms) => edit({ ...draft, arms })}
          />

          <div style={{ marginTop: 12 }}>
            <Messages tone="error" badge="Fix" items={errors.length ? errors : (preview?.errors ?? [])} />
            <Messages tone="warn" badge="Check" items={draftWarnings} />
          </div>

          <div className="ve-foot">
            <div>
              <label htmlFor="ve-note">What changed (goes in the Remote Config description)</label>
              <input
                id="ve-note"
                type="text"
                maxLength={NOTE_MAX}
                value={note}
                placeholder="e.g. Raise eleven to 40%"
                onChange={(e) => setNote(e.target.value)}
              />
            </div>
            <button
              type="button"
              className="btn btn-primary"
              disabled={blockedReason !== null || checking}
              onClick={openReview}
            >
              {checking ? "Checking…" : "Review and publish"}
            </button>
          </div>
          <div className="ve-help">
            {blockedReason ??
              "Studio checks the draft with the server first, then publishes with If-Match so two people cannot overwrite each other."}
          </div>
        </section>

        <div className="ve-aside">
          <DistributionPanel
            preview={preview}
            previewing={previewing}
            error={previewError}
            blocked={errors.length > 0}
          />
          <LookupPanel
            result={lookup && previewFor === lookup ? (preview?.lookup ?? null) : null}
            lookup={lookup}
            error={lookupError}
            blocked={errors.length > 0}
            onLookup={(l) => {
              setLookupError(null);
              setLookup(l);
            }}
          />
        </div>
      </div>

      <HistoryPanel
        history={history}
        error={historyError}
        canWrite={canWrite && !!live?.etag && !live.conditional}
        onEdit={editFromHistory}
        onRestore={(entry) => {
          setPublishErrors([]);
          setRestoreNote("");
          setRestoring(entry);
        }}
      />

      <Modal
        open={review !== null}
        onClose={() => setReview(null)}
        title={`Publish voice_engine on ${env.environment}`}
        wide
        footer={
          <>
            {publishErrors.length > 0 && <span className="error-text">{publishErrors.join(" ")}</span>}
            <div className="spacer" />
            <button type="button" className="btn" onClick={() => setReview(null)}>
              Cancel
            </button>
            <button type="button" className="btn btn-primary" disabled={publishing} onClick={publish}>
              {publishing ? "Publishing…" : "Publish"}
            </button>
          </>
        }
      >
        <p className="muted ve-small" style={{ marginTop: 0 }}>
          The server checked this draft. It publishes over template v{live?.templateVersion.number}; if someone
          published since, you get their change back to review instead.
        </p>
        {replacesLive && (
          <Callout tone="warn" badge={replacesText === "missing" ? "Missing" : "Invalid"}>
            {replacesText === "missing"
              ? "There is no voice_engine value yet, so every call is on the cascade. Publishing creates it from this draft."
              : `The live value is invalid (${live?.error ?? "unreadable"}), so every call is on the cascade. Publishing replaces it with this draft.`}
          </Callout>
        )}
        <DiffList rows={diff} />
        <Messages tone="warn" badge="Check" items={review?.warnings ?? []} />
        <details className="ve-details">
          <summary>Remote Config value (JSON)</summary>
          <pre className="mono ve-json">{review ? JSON.stringify(JSON.parse(review.raw), null, 2) : ""}</pre>
        </details>
        <p className="muted ve-small">
          Description:{" "}
          <span className="mono">
            STUDIO: {userEmail ?? "your email"}: {note.trim()}
          </span>
        </p>
      </Modal>

      <Modal
        open={restoring !== null}
        onClose={() => setRestoring(null)}
        title={`Restore template v${restoring?.version.number ?? ""}`}
        wide
        footer={
          <>
            {publishErrors.length > 0 && <span className="error-text">{publishErrors.join(" ")}</span>}
            <div className="spacer" />
            <button type="button" className="btn" onClick={() => setRestoring(null)}>
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary"
              disabled={publishing || !restoreNote.trim()}
              onClick={restore}
            >
              {publishing ? "Restoring…" : "Restore as a new version"}
            </button>
          </>
        }
      >
        {restoring && (
          <>
            <p className="muted ve-small" style={{ marginTop: 0 }}>
              Publishes v{restoring.version.number}'s value exactly as it was stored, as a new template version. The
              draft above then starts again from the restored value.
            </p>
            {live && <DiffList rows={policyDiff(live.policy, restoring.policy)} />}
            <details className="ve-details">
              <summary>Remote Config value (JSON)</summary>
              <pre className="mono ve-json">{restoring.raw}</pre>
            </details>
            <label htmlFor="ve-restore-note">Why (goes in the Remote Config description)</label>
            <input
              id="ve-restore-note"
              type="text"
              maxLength={NOTE_MAX}
              value={restoreNote}
              placeholder="e.g. Roll eleven back"
              onChange={(e) => setRestoreNote(e.target.value)}
            />
            <p className="muted ve-small">
              Description:{" "}
              <span className="mono">
                STUDIO: {userEmail ?? "your email"}: restore v{restoring.version.number}: {restoreNote.trim()}
              </span>
            </p>
          </>
        )}
      </Modal>
    </div>
  );
}
